/**
 * 鉴权动作（任务 6d）：登录页组件只调这里，不直接碰 workerHost / RPC。
 * host/store 作参数注入（可单测）；组件侧传 appWorker()/globalStore() 单例。
 *
 * 登录成功只写 GlobalState.auth——页面跳转由路由守卫（nav/guard.ts）接管，
 * 组件不做导航。错误统一折成面向用户的中文文案。
 */
import type { AppWorkerHost } from './authState';
import type { GlobalStore } from './global';
import { isNetworkRpcCode, parseApiErrorCode, type WorkerMethods } from '~/worker/contract';
import { RpcError } from '~/worker/rpc/protocol';

/** auth/login 的请求参数——need2fa 时原样暂存，TOTP 页带 totpCode 重提（§3.3 两阶段） */
export type PendingLogin = WorkerMethods['auth/login']['req'];

/** 登录成功的用户摘要（auth/login 回包形状） */
export type LoginUser = Extract<WorkerMethods['auth/login']['res'], { need2fa: false }>['user'];

/**
 * 登录成功的提交覆盖（14.34b-2 添加账号）：默认行为是 store.set(auth) 交守卫导航；
 * 添加账号模式已登录另一账号，直写 auth 会跳过各切片的登出重置路径——改由调用方
 * 跑切换收尾（accountsActions.settleAccountSwitch）。返回 Promise 时等它完成再解除 busy。
 */
export type LoginCommit = (user: LoginUser) => void | Promise<void>;

export type LoginOutcome =
  | { readonly kind: 'ok' }
  /** 已绑定 TOTP 的账号需要两步验证：pending=原登录参数，2FA 页重提用 */
  | { readonly kind: 'need2fa'; readonly pending: PendingLogin }
  | { readonly kind: 'error'; readonly message: string };

// ---------------------------------------------------------------------------
// 验证码登录的账号形态（任务 6d-2）：含 @ 判邮箱，否则按手机号 + 区号。
// 校验口径对齐旧版 login_controller.dart：86 区号校验 1[3-9]\d{9}，
// 其他区号只要非空（服务端兜底；目前仅 86 可发短信，protocol §3.2）。
// ---------------------------------------------------------------------------

export type CodeAccount =
  | { readonly kind: 'phone'; readonly countryCode: number; readonly phone: string }
  | { readonly kind: 'email'; readonly email: string }
  | { readonly kind: 'invalid'; readonly message: string };

export function classifyCodeAccount(
  raw: string,
  countryCode: number,
  /** 邮箱 Tab（21t）：只收邮箱，输手机号也按邮箱格式报错，不去猜 */
  emailOnly = false,
): CodeAccount {
  const text = raw.trim();
  if (text === '') {
    return { kind: 'invalid', message: emailOnly ? '请输入邮箱' : '请输入手机号或邮箱' };
  }
  if (emailOnly || text.includes('@')) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) {
      return { kind: 'invalid', message: '邮箱格式错误' };
    }
    return { kind: 'email', email: text };
  }
  if (countryCode === 86 && !/^1[3-9]\d{9}$/.test(text)) {
    return { kind: 'invalid', message: '手机号格式错误' };
  }
  if (!/^\d+$/.test(text)) return { kind: 'invalid', message: '手机号格式错误' };
  return { kind: 'phone', countryCode, phone: text };
}

/** 统一登录提交：成功写 auth、Need2FA 原样暂存参数（三个 login* 入口共用） */
async function performLogin(
  host: AppWorkerHost,
  store: GlobalStore,
  params: PendingLogin,
  commit?: LoginCommit,
): Promise<LoginOutcome> {
  try {
    const result = await host.call('auth/login', params);
    if (result.need2fa) return { kind: 'need2fa', pending: params };
    if (commit) {
      await commit(result.user);
    } else {
      // needsPassword=true 时守卫会强制 replace 到设密码页（nav/guard.ts），组件不导航
      store.set({ auth: { status: 'logged-in', ...result.user } });
    }
    return { kind: 'ok' };
  } catch (err) {
    return { kind: 'error', message: loginErrorMessage(err) };
  }
}

/** Type=3 密码登录：Username 可为手机号/邮箱/用户名（protocol §3.3） */
export function loginWithPassword(
  host: AppWorkerHost,
  store: GlobalStore,
  params: { account: string; password: string },
  commit?: LoginCommit,
): Promise<LoginOutcome> {
  return performLogin(
    host,
    store,
    {
      type: 3,
      username: params.account.trim(),
      code: params.password,
    },
    commit,
  );
}

/** Type=1 短信 / Type=2 邮箱验证码登录（账号不存在自动注册，protocol §3.3） */
export function loginWithCode(
  host: AppWorkerHost,
  store: GlobalStore,
  account: Exclude<CodeAccount, { kind: 'invalid' }>,
  code: string,
  commit?: LoginCommit,
): Promise<LoginOutcome> {
  return performLogin(
    host,
    store,
    {
      type: account.kind === 'email' ? 2 : 1,
      username: account.kind === 'email' ? account.email : account.phone,
      code,
      ...(account.kind === 'phone' ? { countryCode: account.countryCode } : {}),
    },
    commit,
  );
}

/**
 * 2FA 第二阶段：原登录参数 + TOTP 码整体重提（后端校验失败返回中文 message 直接展示）。
 * 再次返回 need2fa 理论上不可能（TotpCode 非空必走校验分支），折成错误防循环。
 */
export async function loginWith2fa(
  host: AppWorkerHost,
  store: GlobalStore,
  pending: PendingLogin,
  totpCode: string,
  commit?: LoginCommit,
): Promise<LoginOutcome> {
  const outcome = await performLogin(host, store, { ...pending, totpCode }, commit);
  if (outcome.kind === 'need2fa') return { kind: 'error', message: '两步验证失败，请重试' };
  return outcome;
}

// ---------------------------------------------------------------------------
// 扫码登录（任务 6f）：generate → UI 渲染二维码 → 2s 轮询 check（唯一允许的轮询）。
// 过期判定在 UI 本地计时（expiresIn 秒）——Redis 键没了之后 check 不会再返回
// expired 状态（Worker 把业务错折成 pending），本地计时是唯一可靠判据。
// ---------------------------------------------------------------------------

export type QrLoginInfo = WorkerMethods['auth/qrGenerate']['res'];

export async function generateLoginQr(
  host: AppWorkerHost,
): Promise<{ ok: true; qr: QrLoginInfo } | { ok: false; message: string }> {
  try {
    return { ok: true, qr: await host.call('auth/qrGenerate', null) };
  } catch (err) {
    return { ok: false, message: loginErrorMessage(err, '二维码生成失败，请重试') };
  }
}

export type QrPollOutcome = 'pending' | 'expired' | 'confirmed';

/** 单次轮询。confirmed 时写 auth（守卫接管导航）；网络错误折成 pending 继续轮 */
export async function checkLoginQr(
  host: AppWorkerHost,
  store: GlobalStore,
  qrcodeId: string,
): Promise<QrPollOutcome> {
  try {
    const result = await host.call('auth/qrCheck', { qrcodeId });
    if (result.status === 'confirmed') {
      store.set({ auth: { status: 'logged-in', ...result.user } });
      return 'confirmed';
    }
    return result.status;
  } catch {
    // 断网等瞬时故障：继续轮询，恢复后自动接上；真过期由本地计时兜住
    return 'pending';
  }
}

export type SendCodePurpose = 'login' | 'find_password' | 'bind';

/** 发送验证码（调用方先过防水墙拿 ticket/randstr；Worker 内部先 ensureIpAnchor）。
 *  bind = 绑手机/绑邮箱/换邮箱（14.29c；§7.5——bind 系验证码无场景隔离，
 *  与登录码同走 verification_code，CodeType 只路由发送通道） */
export async function sendLoginCode(
  host: AppWorkerHost,
  account: Exclude<CodeAccount, { kind: 'invalid' }>,
  captcha: { ticket: string; randstr: string },
  purpose: SendCodePurpose = 'login',
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await host.call('auth/sendCode', {
      codeType: account.kind === 'email' ? `email_${purpose}` : `phone_${purpose}`,
      // 手机号形态：Account = 区号+号码（protocol §3.2）
      account: account.kind === 'email' ? account.email : `${account.countryCode}${account.phone}`,
      ticket: captcha.ticket,
      randstr: captcha.randstr,
    });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: loginErrorMessage(err, '验证码发送失败，请稍后重试') };
  }
}

/** 找回/重置密码（protocol §3.5；先用 *_find_password 验证码，确认密码同值由 Worker 填充） */
export async function resetPassword(
  host: AppWorkerHost,
  account: Exclude<CodeAccount, { kind: 'invalid' }>,
  code: string,
  newPassword: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await host.call('auth/findPassword', {
      type: account.kind === 'email' ? 2 : 1,
      username: account.kind === 'email' ? account.email : account.phone,
      code,
      newPassword,
      ...(account.kind === 'phone' ? { countryCode: account.countryCode } : {}),
    });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: loginErrorMessage(err, '重置密码失败，请稍后重试') };
  }
}

/** 首登强制设密码提交（6e-2）：成功清 needsPassword，守卫自动离开设密码页 */
export async function setInitialPassword(
  host: AppWorkerHost,
  store: GlobalStore,
  newPassword: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await host.call('user/setPassword', { newPassword });
    const auth = store.get().auth;
    if (auth.status === 'logged-in') {
      store.set({ auth: { ...auth, needsPassword: false } });
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, message: loginErrorMessage(err, '设置失败，请稍后重试') };
  }
}

/** 主动登出（设密码页的逃脱口等）：本地必成功，守卫自动回登录页 */
export async function logoutToLogin(host: AppWorkerHost, store: GlobalStore): Promise<void> {
  try {
    await host.call('auth/logout', null);
  } catch {
    // Worker 死亡等极端情形：本地状态照常置登出，下次启动以 Worker 持久层为准
  }
  store.set({ auth: { status: 'logged-out' } });
}

/**
 * 永久删除（注销）账号（21e-2）。成功后与登出同样置 auth=logged-out 交守卫回登录页；
 * **失败返回服务端文案**由调用方 toast——注销不能像登出那样「失败也当成功」，
 * 否则用户以为账号没了、实际还在。
 */
export async function cancelAccountToLogin(
  host: AppWorkerHost,
  store: GlobalStore,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await host.call('auth/cancelAccount', null);
  } catch (err) {
    return { ok: false, message: loginErrorMessage(err, '删除失败，请稍后重试') };
  }
  store.set({ auth: { status: 'logged-out' } });
  return { ok: true };
}

export function loginErrorMessage(err: unknown, fallback = '登录失败，请稍后重试'): string {
  if (err instanceof RpcError) {
    const apiCode = parseApiErrorCode(err.code);
    if (apiCode !== undefined) {
      // 业务失败的 message 本身是后端面向用户的中文文案（如「密码错误」），直接展示
      return err.message || `${fallback}（${apiCode}）`;
    }
    if (isNetworkRpcCode(err.code)) return '网络异常，请检查网络后重试';
  }
  return fallback;
}
